import * as React from 'react';
import { View } from 'react-native';

import { colors } from './constants';
import type { PropsOf } from './propsOf';

export interface BackgroundViewOwnProps<E extends React.ElementType = React.ElementType> {
    backgroundColor?: string;
    children?: React.ReactNode;
    component?: E;
}

export type BackgroundViewProps<E extends React.ElementType = React.ElementType> =
    BackgroundViewOwnProps<E> & Omit<PropsOf<E>, keyof BackgroundViewOwnProps>;

export const BackgroundViewContext = React.createContext(colors.WHITE);

/**
 * Basic view with a background. The background color can be access by lower components
 * using "useBackgroundColor".
 *
 * This component is useful for easy styling and allow powerful hover effect in children.
 */
export const BackgroundView = React.forwardRef(function BackgroundView<E extends React.ElementType>(
    props: BackgroundViewProps<E>,
    ref: React.Ref<E>
): React.ReactElement {
    const { component: C = View, backgroundColor, style, children, ...viewProps } = props;

    const content = (
        // @ts-ignore
        <C ref={ref} {...viewProps} style={[style, backgroundColor && { backgroundColor }]}>
            {children}
        </C>
    );

    if (!backgroundColor) {
        return content;
    }

    return (
        <BackgroundViewContext.Provider value={backgroundColor}>
            {content}
        </BackgroundViewContext.Provider>
    );
});

/**
 * React hook to use the current background color.
 */
export function useBackgroundColor(): string {
    return React.useContext(BackgroundViewContext);
}
